// src/components/ResetPassword.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import './ResetPassword.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Validate token on mount
  useEffect(() => {
    validateToken();
  }, [token, email]);

  const validateToken = async () => {
    if (!token || !email) {
      setError('Ongeldige reset link. Vraag een nieuwe aan.');
      setIsValidating(false);
      return;
    }

    try {
      const resetDocRef = doc(db, 'password_resets', token);
      const resetDoc = await getDoc(resetDocRef);

      if (!resetDoc.exists()) {
        setError('Deze reset link is ongeldig of al gebruikt.');
        setIsValidating(false);
        return;
      }

      const resetData = resetDoc.data();

      // Check if already used
      if (resetData.used) {
        setError('Deze reset link is al gebruikt. Vraag een nieuwe aan.');
        setIsValidating(false);
        return;
      }

      // Check if expired
      const expiresAt = resetData.expiresAt.toDate();
      if (new Date() > expiresAt) {
        setError('Deze reset link is verlopen. Vraag een nieuwe aan.');
        setIsValidating(false);
        return;
      }

      // Check if email matches
      if (resetData.email !== email) {
        setError('Deze reset link is niet geldig voor dit email adres.');
        setIsValidating(false);
        return;
      }

      console.log('✅ Reset token is valid');
      setIsValidating(false);

    } catch (error) {
      console.error('Token validation error:', error);
      setError('Er is een fout opgetreden bij het valideren van de link.');
      setIsValidating(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!password) {
      errors.password = 'Wachtwoord is verplicht';
    } else if (password.length < 12) {
      errors.password = 'Wachtwoord moet minimaal 12 karakters zijn';
    } else if (!/[A-Z]/.test(password)) {
      errors.password = 'Wachtwoord moet minimaal 1 hoofdletter bevatten';
    } else if (!/[a-z]/.test(password)) {
      errors.password = 'Wachtwoord moet minimaal 1 kleine letter bevatten';
    } else if (!/[0-9]/.test(password)) {
      errors.password = 'Wachtwoord moet minimaal 1 cijfer bevatten';
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.password = 'Wachtwoord moet minimaal 1 speciaal teken bevatten';
    }

    if (password !== confirmPassword) {
      errors.confirmPassword = 'Wachtwoorden komen niet overeen';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);
    setValidationErrors({});

    try {
      console.log('🔐 Calling password reset Cloud Function...');

      // Call Cloud Function to reset password
      const response = await fetch('https://us-central1-relatiescan.cloudfunctions.net/resetPasswordWithToken', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          email: email,
          newPassword: password
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reset password');
      }

      console.log('✅ Password reset successful');
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Je wachtwoord is gereset. Log in met je nieuwe wachtwoord.' 
          } 
        });
      }, 3000);

    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Er is een fout opgetreden bij het resetten van je wachtwoord.');
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Link valideren...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !isLoading) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h2>Reset link ongeldig</h2>
            <p>{error}</p>
            <Link to="/login" className="btn-primary">
              Terug naar inloggen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="success-state">
            <div className="success-icon">✅</div>
            <h2>Wachtwoord gereset!</h2>
            <p>Je wachtwoord is succesvol gewijzigd. Je wordt doorgestuurd naar de login pagina...</p>
            <Link to="/login" className="btn-primary">
              Ga naar inloggen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h1>Nieuw wachtwoord instellen</h1>
          <p>Voer je nieuwe wachtwoord in voor {email}</p>
        </div>

        <div className="reset-password-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">
                Nieuw wachtwoord
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`form-input ${validationErrors.password ? 'error' : ''}`}
                placeholder="Minimaal 12 karakters"
              />
              <div className="help-text">
                Je wachtwoord moet voldoen aan:
                <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                  <li>Minimaal 12 karakters</li>
                  <li>Minimaal 1 hoofdletter (A-Z)</li>
                  <li>Minimaal 1 kleine letter (a-z)</li>
                  <li>Minimaal 1 cijfer (0-9)</li>
                  <li>Minimaal 1 speciaal teken (!@#$%^&*)</li>
                </ul>
              </div>
              {validationErrors.password && (
                <div className="error-message">{validationErrors.password}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                Bevestig wachtwoord
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`form-input ${validationErrors.confirmPassword ? 'error' : ''}`}
                placeholder="Herhaal je nieuwe wachtwoord"
              />
              {validationErrors.confirmPassword && (
                <div className="error-message">{validationErrors.confirmPassword}</div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Wachtwoord resetten...' : 'Wachtwoord resetten'}
            </button>
          </form>
        </div>

        <div className="reset-password-footer">
          <Link to="/login" className="footer-link">
            ← Terug naar inloggen
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;